import BookingExpiryContent from '@/components/header/in-progress-booking-cart/BookingExpiryContent';
import MobileLanguageSelect from '@/components/header/language-select/MobileLanguageSelect';
import Logo from '@/components/header/Logo';
import TenantSelect from '@/components/header/nav-menu/TenantSelect';
import { Link } from '@/components/primitives/Link';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useAuth } from '@/hooks/useAuth';
import { useInProgressBooking } from '@/hooks/useInProgressBooking';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import { useSelector } from '@/store/utils';
import type { NavigationItem } from '@/types/navigation';
import { cn } from '@/utils/cn';
import { isLocalDevelopment } from '@/utils/isLocalDev';
import { getDisplayName } from '@/utils/profile';
import { getTenantConfiguration } from '@/utils/tenant';
import {
  Button,
  Divider,
  Icons,
  Typography,
  typographyVariants,
} from '@fleet/webshop-ui';
import { useState, type FC } from 'react';
import { useLocation } from 'react-router-dom';

type MobileNavMenuProps = {
  menuItems: Array<NavigationItem>;
  userMenuItems?: Array<[TransTextKeys, string, React.ReactNode]>;
};

const IrishRailLogo = () => (
  <img src="/irishrail_logo.jpg" alt="Irish Rail" className="h-16 w-auto" />
);

const MobileNavMenu: FC<MobileNavMenuProps> = ({
  menuItems,
  userMenuItems,
}) => {
  const selectedTenant = useSelector(tenantSelector);
  const TenantIcon =
    selectedTenant.value === 'irishrail'
      ? IrishRailLogo
      : Icons[selectedTenant.value];
  const configuration = getTenantConfiguration(selectedTenant.value);
  const { isAuthenticated, logout } = useAuth();
  const profile = useSelector(profileDataSelector);
  const [menuOpen, setMenuOpen] = useState(false);
  const mainMenuItems = menuItems.filter((item) => item.path !== '/login');
  const location = useLocation();
  const { bookingId, isValidating, countdown, handleCheckoutClick } =
    useInProgressBooking();

  return (
    <div
      className="mt-1 flex w-full items-center justify-between gap-2 tablet:px-0"
      data-testid="navbar-mobile"
    >
      <Logo />

      <div className="flex h-6 items-center gap-4">
        <MobileLanguageSelect />
        {isLocalDevelopment() && (
          <>
            <Divider
              orientation="vertical"
              className="h-4"
              variant="strong"
              grow={false}
            />
            <TenantSelect />
          </>
        )}
        <Divider
          orientation="vertical"
          className="h-4"
          variant="strong"
          grow={false}
        />
        <Button
          variant="ghostLink"
          data-testid="mobile-nav-open-button"
          aria-label="Open navigation menu"
          onClick={() => setMenuOpen(true)}
        >
          <span className="relative">
            <Icons.burgerMenu width={24} height={24} />
            {bookingId && !isValidating && (
              <span
                className="absolute right-[-2px] top-[-2px] h-2 w-2 rounded-full bg-error-text"
                aria-hidden="true"
              />
            )}
          </span>
        </Button>
      </div>
      {menuOpen && (
        <div className="fixed inset-0 z-[51] min-w-mobile">
          <div className="flex h-full w-full flex-col bg-background px-4">
            <div className="flex h-14 items-center">
              <TenantIcon />
              <div className="ml-auto flex h-6 w-fit items-center gap-4">
                <Button
                  variant="ghostLink"
                  aria-label="Close navigation menu"
                  data-testid="mobile-nav-close-button"
                  onClick={() => setMenuOpen(false)}
                >
                  <Icons.x width={24} height={24} />
                </Button>
              </div>
            </div>
            {bookingId && !isValidating && (
              <div className="my-3 w-full rounded border border-info-border bg-white p-3">
                <BookingExpiryContent
                  countdown={countdown}
                  onCheckoutClick={() =>
                    handleCheckoutClick(() => setMenuOpen(false))
                  }
                />
              </div>
            )}
            <nav className="flex flex-col py-6">
              {isAuthenticated ? (
                <>
                  <div className="pb-4">
                    <Typography
                      variant="mobile-title"
                      className="text-foreground"
                    >
                      <TransText
                        i18nKey="helloDisplayName"
                        values={{ displayName: getDisplayName(profile) }}
                      />
                    </Typography>
                  </div>

                  {userMenuItems?.map(([i18nKey, path, icon]) => {
                    const isActive =
                      path !== 'logout' && location.pathname === path;
                    const handleClick = () => {
                      setMenuOpen(false);
                      if (path === 'logout') {
                        return logout();
                      }
                    };

                    if (path === 'logout') {
                      return (
                        <Button
                          key={path}
                          variant="ghostLink"
                          onClick={handleClick}
                          data-testid={`mobile-nav-link-${i18nKey}`}
                          className="flex h-14 w-full items-center justify-start gap-4 py-4"
                        >
                          {icon}
                          <Typography variant="subtitle">
                            <TransText i18nKey={i18nKey} />
                          </Typography>
                          <span
                            data-testid={`mobile-nav-link-${i18nKey}-button`}
                            className="ml-auto text-primary"
                          >
                            <Icons.chevronRight width={24} height={24} />
                          </span>
                        </Button>
                      );
                    }

                    return (
                      <Link
                        key={path}
                        to={path}
                        className="flex h-14 items-center gap-4 py-4"
                        onClick={() => setMenuOpen(false)}
                        active={isActive}
                      >
                        {icon}
                        <Typography variant="subtitle">
                          <TransText i18nKey={i18nKey} />
                        </Typography>
                        <Button
                          variant="ghostLink"
                          data-testid={`mobile-nav-link-${i18nKey}-button`}
                          className="ml-auto text-primary"
                        >
                          <Icons.chevronRight width={24} height={24} />
                        </Button>
                      </Link>
                    );
                  })}
                </>
              ) : (
                configuration?.showLogin && (
                  <Link
                    to="/login"
                    className="flex items-center gap-4 py-4"
                    onClick={() => setMenuOpen(false)}
                    active={location.pathname === '/login'}
                  >
                    <Icons.user width={24} height={24} />
                    <Typography variant="subtitle">
                      <TransText i18nKey="logIn" />
                    </Typography>
                    <Button
                      variant="ghostLink"
                      data-testid="mobile-nav-link-login-button"
                      className="ml-auto text-primary"
                    >
                      <Icons.chevronRight width={24} height={24} />
                    </Button>
                  </Link>
                )
              )}
              <Divider className="my-4" />
              {mainMenuItems.map((item) => {
                const isActive = location.pathname.includes(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      'h-14 py-4',
                      typographyVariants({ variant: 'subtitle' })
                    )}
                    active={isActive}
                  >
                    {item.name}
                    <Button
                      variant="ghostLink"
                      data-testid="mobile-nav-link-button"
                      className="ml-auto text-primary"
                    >
                      <Icons.chevronRight width={24} height={24} />
                    </Button>
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileNavMenu;
